import React, { useState, useRef, useCallback, useLayoutEffect, useMemo } from 'react';
import Icon from '../shared/Icon';
import { useFlag } from '@unleash/proxy-client-react';
import './ExtractionTable.css';
import { toCsv, toCsvAsync, toCsvRowsAsync, buildCsvFilename, downloadCsv, appendChecksSection } from '../../../utils/csv';
import { capDisplayDecimals } from '../../../utils/numberFormat';
import { apiService } from '../../../services/api';

// Hard cap on rows rendered in the chat preview. Beyond this, users see
// "Showing N of M rows" and must Download as CSV for the rest. Only
// VISIBLE_ROW_COUNT show at once and the rest of the preview is a scroll away,
// the same way a widget table reads.
const PREVIEW_ROW_LIMIT = 25;
const VISIBLE_ROW_COUNT = 10;
const ASYNC_EXPORT_THRESHOLD = 5000;
// Most rows the server puts in one export of a chat result. Mirrors the server's cap,
// which is what actually enforces it: this copy only sets the button label and the file
// name ahead of the request, so it has to change whenever the server's does.
export const EXPORT_ROW_CAP = 50000;

// Empty for the purposes of the record list: what formatValue would draw as an em-dash,
// plus the empty string. Not applied to the table, whose blank cells read as blank.
const isBlank = (value) => value === null || value === undefined || value === '';

const NO_DECIMAL_COLUMNS = new Set();

// How the server writes an empty cell in exported rows. The file writes it blank, the
// same as the in-browser CSV writes an empty value.
const EXPORT_NULL_CELL = '__NULL__';

// "6,356", or "more than 500" when the server could only bound the total. Never the
// number of rows loaded, which is the cap and not a count.
export const formatTotal = (total, exact = true) => (
  exact === false ? `more than ${Math.max(total - 1, 0).toLocaleString()}` : total.toLocaleString()
);

// The server's own sentence where it sent one. Read from the parsed body rather than the
// error's message, which the API layer fills with a generic line when the body is an
// error page or carries no detail; a non-string detail (a validation list) is not a sentence.
const serverDetail = (err) => (typeof err?.responseData?.detail === 'string' ? err.responseData.detail : '');

const exportErrorMessage = (err) => {
  if (err?.status === 410) return 'The dataset behind this result is no longer available.';
  if (err?.status === 403) return 'You no longer have access to the data behind this result.';
  if (err?.status === 409) return serverDetail(err) || 'This result cannot be downloaded in full.';
  // Too large to export in time. Asking again would time out again, so the fallback
  // says to narrow the question rather than to retry.
  if (err?.status === 422 || err?.status === 504) {
    return serverDetail(err) || 'This result is too large to download in time. Narrow the question to download it.';
  }
  return err?.message || 'The download could not be produced.';
};

// Plain coercion for CSV cells. Distinct from the UI formatter — the file
// should hold raw values, not display affordances like ✓ / em-dash.
const csvFormatValue = (value, key) => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'object') {
    if (Array.isArray(value)) {
      return value.map((v) => (typeof v === 'object' ? JSON.stringify(v) : String(v))).join(', ');
    }
    if (key === 'source_file') return value.file_name || value.name || JSON.stringify(value);
    return JSON.stringify(value);
  }
  return String(value);
};

// Cell value for display. Only a column the server names as a computed decimal is
// rounded (to two places); every other cell is shown as written. Rounding by shape
// instead would also catch a figure quoted from a document, which arrives as the same
// plain string ("0.125") and must not change.
export const formatValue = (value, key, decimalColumns = NO_DECIMAL_COLUMNS) => {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'boolean') return value ? '✓' : '✗';
  if (typeof value === 'object') {
    if (Array.isArray(value)) {
      return value.map((v) => typeof v === 'object' ? JSON.stringify(v) : String(v)).join(', ');
    }
    if (key === 'source_file') return value.file_name || value.name || JSON.stringify(value);
    return JSON.stringify(value, null, 2);
  }
  if (decimalColumns.has(key)) return capDisplayDecimals(value);
  return String(value);
};

/**
 * Renders extraction results as an expandable/scrollable table.
 * Keys become columns, each file's results become a row.
 *
 * A query result can be longer than the rows it arrived with. Then `fullTotal` is its
 * true size (a lower bound when `fullTotalExact` is false), the header says "K of N",
 * and the download fetches every row from the server by `resultRef` -- or, for a result
 * that cannot be fetched whole, downloads the loaded rows and says they are partial.
 * `summaryData` is an aggregate of the same full result, shown first when `primary` is
 * "summary". All of these are optional: absent, the table behaves as it always has.
 */
const ExtractionTable = ({
  data,
  title = "Extraction Results",
  variant = "default",
  evidenceOpen = false,
  filenameBase,
  checks,
  decimalColumns,
  fullTotal,
  fullTotalExact = true,
  resultRef,
  threadId,
  summaryData,
  summaryDescription,
  primary,
  onShowNotification,
}) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const csvExportEnabled = useFlag('extraction_csv_export');
  const wrapperRef = useRef(null);
  const [exporting, setExporting] = useState(false);
  // State alone lets a second click through when both land before the re-render that
  // disables the button; the ref is set synchronously.
  const exportingRef = useRef(false);
  const [wrapperMaxHeight, setWrapperMaxHeight] = useState(null);
  const hasSummary = Array.isArray(summaryData) && summaryData.length > 0;
  const [showSummary, setShowSummary] = useState(hasSummary && primary === 'summary');
  const summaryShown = hasSummary && showSummary;

  // Normalize data to array of objects
  const fullRows = useMemo(() => (Array.isArray(data) ? data : [data]), [data]);
  const rows = summaryShown ? summaryData : fullRows;
  // Optional: absent on older threads and on tables parsed out of Markdown, which then
  // render exactly as written.
  const decimalSet = useMemo(
    () => (Array.isArray(decimalColumns) && decimalColumns.length ? new Set(decimalColumns) : NO_DECIMAL_COLUMNS),
    [decimalColumns],
  );

  // Derive columns: union of non-internal keys. Empty-in-every-row columns are kept
  // so every requested field renders (and exports), even when no doc has a value.
  const columns = useMemo(() => {
    const allKeys = new Set();
    rows.forEach((row) => {
      if (row && typeof row === 'object') {
        Object.keys(row).forEach((key) => {
          if (!['id', 'org_id', 'file_id', 'dataset_id', '_metadata', 'evidence'].includes(key)) {
            allKeys.add(key);
          }
        });
      }
    });
    return Array.from(allKeys);
  }, [rows]);

  const totalRows = rows.length;
  // The full result's size when the server says it is longer than the rows loaded.
  const resultTotal = Number.isFinite(fullTotal) && fullTotal > fullRows.length ? fullTotal : null;
  const isPartial = !summaryShown && resultTotal !== null;
  const fullTotalLabel = resultTotal !== null ? formatTotal(resultTotal, fullTotalExact) : fullRows.length.toLocaleString();
  const canFetchAll = isPartial && !!resultRef && !!threadId;
  // Known before the request to be longer than one export holds. A bounded total
  // ("more than N") is still a lower bound, so exceeding the cap is certain either way.
  const exceedsExportCap = canFetchAll && resultTotal > EXPORT_ROW_CAP;
  // Rows actually placed in the table. Beyond PREVIEW_ROW_LIMIT the user has
  // to download the CSV — surfaced via the "Showing N of M" count.
  const previewRows = useMemo(
    () => (totalRows > PREVIEW_ROW_LIMIT ? rows.slice(0, PREVIEW_ROW_LIMIT) : rows),
    [rows, totalRows],
  );
  const previewCount = previewRows.length;
  // One row is a record, not a table. A grid around it makes the reader scan a header
  // line to find out what the single value underneath means, and a wide one (a matched
  // row can carry a dozen columns) scrolls sideways to do it. Read it as a bullet list
  // instead, and drop the meta strip with it: "Showing 1 of 1 rows" says nothing, and a
  // handful of bullets is not a file worth downloading. Results small enough to belong
  // in the prose never reach the renderer at all -- that is decided upstream -- so this
  // is only about how a survivor is drawn.
  const isSingleRecord = totalRows === 1;

  // Cut the scroller off at the bottom of the tenth row. Measured rather than worked
  // out: the CSS row height is only a minimum, and padding and line-height make the
  // real rows taller, so a fixed figure shows nine and a bit. Measured again whenever
  // the scroller resizes -- it opens through a height animation, during which a
  // sideways scrollbar measures zero, and the chat can be resized after that. The
  // re-measure waits a frame so the new height lands outside the observer callback.
  useLayoutEffect(() => {
    const el = wrapperRef.current;
    if (!el) {
      setWrapperMaxHeight(null);
      return undefined;
    }
    const measure = () => {
      const bodyRows = el.querySelectorAll('tbody tr');
      if (bodyRows.length <= VISIBLE_ROW_COUNT) {
        setWrapperMaxHeight(null);
        return;
      }
      // From the wrapper's own top, not the table's: chat styles give the table a top
      // margin. The tail term is the bottom border plus any sideways scrollbar.
      const lastVisibleBottom = bodyRows[VISIBLE_ROW_COUNT - 1].getBoundingClientRect().bottom;
      const fromWrapperTop = lastVisibleBottom - el.getBoundingClientRect().top + el.scrollTop;
      const height = fromWrapperTop + (el.offsetHeight - el.clientHeight - el.clientTop);
      // No layout (jsdom, a hidden tab) measures zero; fall back to the CSS height.
      setWrapperMaxHeight(height > 0 ? Math.ceil(height) : null);
    };
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    let frame = 0;
    const ro = new ResizeObserver(() => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(measure);
    });
    ro.observe(el);
    return () => {
      cancelAnimationFrame(frame);
      ro.disconnect();
    };
  }, [previewRows, columns, isExpanded]);

  const handleExport = useCallback(async () => {
    if (!csvExportEnabled || exportingRef.current) return;
    exportingRef.current = true;
    setExporting(true);
    const base = filenameBase || title;
    try {
      if (canFetchAll) {
        // Every row, re-run on the server from the result's own query. Kept positional:
        // keying by column name would drop a column whenever two share a name.
        const result = await apiService.exportChatResultRows(threadId, resultRef);
        const fetched = (result?.rows || []).map((r) => r.map((c) => (c === EXPORT_NULL_CELL ? '' : c)));
        const csv = await toCsvRowsAsync(result?.columns || [], fetched);
        const truncated = !!result?.truncated || exceedsExportCap;
        // A file missing rows must not present itself as the whole answer: its name says
        // it is the first part, and the notice stays until it is closed.
        const name = truncated ? `${base} first ${fetched.length} rows` : base;
        downloadCsv(appendChecksSection(csv, checks), buildCsvFilename(name));
        if (truncated) {
          const total = Number.isFinite(result?.total_rows) && result.total_rows > fetched.length
            ? result.total_rows.toLocaleString()
            : fullTotalLabel;
          onShowNotification?.(
            `This result has ${total} rows; the download holds the first ${fetched.length.toLocaleString()}. Narrow the question to export the rest.`,
            'alert',
            { duration: 0 },
          );
        }
        return;
      }
      const csv = totalRows > ASYNC_EXPORT_THRESHOLD
        ? await toCsvAsync(columns, rows, csvFormatValue)
        : toCsv(columns, rows, csvFormatValue);
      // A result that cannot be fetched whole downloads what was loaded, and both the
      // file name and a notice say it is only the first part.
      const name = isPartial ? `${base} first ${totalRows} rows` : base;
      downloadCsv(appendChecksSection(csv, checks), buildCsvFilename(name));
      if (isPartial) {
        onShowNotification?.(
          `Downloaded the first ${totalRows.toLocaleString()} of ${fullTotalLabel} rows. This result cannot be downloaded in full yet.`,
          'info',
        );
      }
    } catch (err) {
      onShowNotification?.(exportErrorMessage(err), 'error');
    } finally {
      exportingRef.current = false;
      setExporting(false);
    }
  }, [
    csvExportEnabled, columns, rows, totalRows, title, filenameBase, checks,
    canFetchAll, exceedsExportCap, threadId, resultRef, isPartial, fullTotalLabel, onShowNotification,
  ]);

  // All hooks above this guard so render order stays stable across data changes.
  if (totalRows === 0 || columns.length === 0) return null;

  // Format column header (snake_case → Title Case)
  const formatHeader = (key) => {
    return key
      .replace(/_/g, ' ')
      .replace(/([A-Z])/g, ' $1')
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ')
      .trim();
  };

  const isDataVariant = variant === 'data';
  const showRowNumber = rows.length > 1;
  // The hint points at the download, so it goes wherever the download goes; the count
  // in front of it stays either way.
  const showTruncationHint = csvExportEnabled && (isPartial || totalRows > PREVIEW_ROW_LIMIT);
  let truncationHint = 'Download all';
  if (isPartial && !canFetchAll) truncationHint = `Download holds the first ${totalRows.toLocaleString()}`;
  if (exceedsExportCap) truncationHint = `Download holds the first ${EXPORT_ROW_CAP.toLocaleString()}`;
  let exportLabel = 'Download as CSV';
  if (canFetchAll) {
    exportLabel = exceedsExportCap ? `Download first ${EXPORT_ROW_CAP.toLocaleString()} (CSV)` : 'Download all (CSV)';
  }

  return (
    <div className={`extraction-table-container ${isExpanded ? 'expanded' : 'collapsed'}${isDataVariant ? ' extraction-table-data' : ''}`}>
      {!isDataVariant && (
        <div className="extraction-table-header" onClick={() => setIsExpanded(!isExpanded)}>
          <span className="extraction-table-icon">
            {isExpanded ? '▼' : '▶'}
          </span>
          <span className="extraction-table-title">{title}</span>
        </div>
      )}

      {(isDataVariant || isExpanded) && (
        <>
          {(!isSingleRecord || hasSummary) && (
            <div className="extraction-table-meta">
              {summaryShown ? (
                <span className="extraction-table-meta-count">
                  Summary{summaryDescription ? `: ${summaryDescription}` : ''} ({totalRows.toLocaleString()} row{totalRows === 1 ? '' : 's'})
                </span>
              ) : (
                <span className="extraction-table-meta-count">
                  Showing {previewCount.toLocaleString()} of {isPartial ? fullTotalLabel : totalRows.toLocaleString()} row{!isPartial && totalRows === 1 ? '' : 's'}
                  {showTruncationHint && <span className="extraction-table-meta-hint"> · {truncationHint}</span>}
                </span>
              )}
              <span className="extraction-table-meta-actions">
                {hasSummary && (
                  <button
                    type="button"
                    className="extraction-table-export-btn"
                    onClick={() => setShowSummary((v) => !v)}
                  >
                    {summaryShown ? `Show full list (${fullTotalLabel})` : 'Show summary'}
                  </button>
                )}
                {csvExportEnabled && (
                  <button
                    type="button"
                    className="extraction-table-export-btn"
                    onClick={handleExport}
                    disabled={exporting}
                    aria-busy={exporting}
                  >
                    <Icon name={exporting ? 'hourglass_empty' : 'file_download'} />
                    {exporting ? 'Preparing…' : exportLabel}
                  </button>
                )}
              </span>
            </div>
          )}
          {summaryShown && (
            <p className="extraction-table-summary-note">
              The full list has {fullTotalLabel} rows; this summary groups them.
            </p>
          )}
          {isSingleRecord ? (
            <dl className="extraction-record">
              {/* Blank fields are dropped here but kept in the table. A grid gives an
                  empty value an empty slot the eye skips; a bullet list would give it a
                  marker, a line and the same weight as a real one, so a wide record
                  turns into a list of em-dashes with the content buried in it. */}
              {columns.filter((col) => !isBlank(rows[0]?.[col])).map((col) => {
                const value = rows[0]?.[col];
                const isComplex = typeof value === 'object' && value !== null;
                return (
                  <div className="extraction-record-row" key={col}>
                    <dt title={col}>{formatHeader(col)}</dt>
                    <dd
                      className={isComplex ? 'complex-value' : ''}
                      title={isComplex ? JSON.stringify(value, null, 2) : undefined}
                    >
                      {formatValue(value, col, decimalSet)}
                    </dd>
                  </div>
                );
              })}
            </dl>
          ) : (
            <div
              className="extraction-table-wrapper"
              ref={wrapperRef}
              style={wrapperMaxHeight ? { maxHeight: wrapperMaxHeight } : undefined}
            >
              <table className="extraction-table">
                <thead>
                  <tr>
                    {showRowNumber && <th className="row-number">#</th>}
                    {columns.map(col => (
                      <th key={col} title={col}>{formatHeader(col)}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {previewRows.map((row, rowIndex) => {
                    return (
                      <tr key={rowIndex}>
                        {showRowNumber && <td className="row-number">{rowIndex + 1}</td>}
                        {columns.map(col => {
                          const value = row?.[col];
                          const isComplex = typeof value === 'object' && value !== null;
                          return (
                            <td
                              key={col}
                              className={isComplex ? 'complex-value' : ''}
                              title={isComplex ? JSON.stringify(value, null, 2) : undefined}
                            >
                              {formatValue(value, col, decimalSet)}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          {evidenceOpen && rows.some(row => row && row.evidence) && (
            <div className="extraction-evidence">
              <details open>
                <summary>Evidence</summary>
                <ul>
                  {rows.flatMap((row, ri) => {
                    if (!row || !row.evidence) return [];
                    const ev = row.evidence;
                    const sourceTag = row.source_file
                      ? <span className="evidence-source-tag">{row.source_file}</span>
                      : null;
                    if (typeof ev === 'string') {
                      return [(
                        <li key={`${ri}-string`} style={{ whiteSpace: 'pre-line' }}>
                          {sourceTag}{ev}
                        </li>
                      )];
                    }
                    if (typeof ev !== 'object') return [];
                    const firstVal = Object.values(ev)[0];
                    if (typeof firstVal !== 'object' || firstVal === null) {
                      return [(
                        <li key={`${ri}-flat`}>
                          {sourceTag}
                          {ev.page && <>Page {ev.page}</>}
                          {ev.quote && <> — <em>"{ev.quote}"</em></>}
                        </li>
                      )];
                    }
                    return Object.entries(ev).map(([field, fieldEv]) => (
                      <li key={`${ri}-${field}`}>
                        {sourceTag}
                        <strong>{formatHeader(field)}</strong>
                        {fieldEv && fieldEv.page && <> — Page {fieldEv.page}</>}
                        {fieldEv && fieldEv.quote && <> — <em>"{fieldEv.quote}"</em></>}
                      </li>
                    ));
                  })}
                </ul>
              </details>
            </div>
          )}
        </>
      )}
    </div>
  );
};

/**
 * Parse message content to extract JSON blocks for table rendering.
 * Returns { text: string, extractions: array }
 */
export const parseExtractionResults = (content) => {
  if (!content || typeof content !== 'string') {
    return { text: content, extractions: [] };
  }

  const extractions = [];
  let text = content;
  const removedRanges = [];

  // Helper to check if parsed JSON looks like extraction data (has data-like keys)
  const isExtractionData = (obj) => {
    if (!obj || typeof obj !== 'object') return false;
    if (obj.error) return false;
    // Arrays of objects are table data
    if (Array.isArray(obj) && obj.length > 0 && typeof obj[0] === 'object') return true;
    // Objects with multiple keys that aren't metadata-only
    const keys = Object.keys(obj);
    if (keys.length === 0) return false;
    // Skip if it only has metadata fields
    const metadataOnly = keys.every(k => ['type', 'status', 'message', 'error'].includes(k));
    return !metadataOnly;
  };

  // Inject file_name into extracted_data so it appears as a table column
  const injectFileName = (data, fileName) => {
    if (!fileName) return data;
    if (Array.isArray(data)) return data.map(row => ({ file_name: fileName, ...row }));
    if (typeof data === 'object') return { file_name: fileName, ...data };
    return data;
  };

  // Pattern 1: ```json ... ``` blocks
  const jsonBlockRegex = /```json\s*([\s\S]*?)```/g;
  let match;

  while ((match = jsonBlockRegex.exec(content)) !== null) {
    try {
      const parsed = JSON.parse(match[1].trim());
      if (parsed && typeof parsed === 'object') {
          if (parsed.extracted_data) {
          extractions.push({
            data: injectFileName(parsed.extracted_data, parsed.file_name),
            title: parsed.file_name || 'Extraction Results'
          });
          removedRanges.push({ start: match.index, end: match.index + match[0].length });
        } else if (isExtractionData(parsed)) {
          extractions.push({ data: parsed, title: 'Extraction Results' });
          removedRanges.push({ start: match.index, end: match.index + match[0].length });
        }
      }
    } catch (e) {
      // Not valid JSON, keep in text
    }
  }

  // Pattern 2: ``` ... ``` blocks (no language specified but might be JSON)
  const genericBlockRegex = /```\s*([\s\S]*?)```/g;

  while ((match = genericBlockRegex.exec(content)) !== null) {
    // Skip if already processed as json block
    if (removedRanges.some(r => match.index >= r.start && match.index < r.end)) continue;
    try {
      const trimmed = match[1].trim();
      if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
        const parsed = JSON.parse(trimmed);
        if (parsed && typeof parsed === 'object') {
          if (parsed.extracted_data) {
            extractions.push({
              data: injectFileName(parsed.extracted_data, parsed.file_name),
              title: parsed.file_name || 'Extraction Results'
            });
            removedRanges.push({ start: match.index, end: match.index + match[0].length });
          } else if (isExtractionData(parsed)) {
            extractions.push({ data: parsed, title: 'Extraction Results' });
            removedRanges.push({ start: match.index, end: match.index + match[0].length });
          }
        }
      }
    } catch (e) {
      // Not valid JSON
    }
  }

  // Pattern 3: Standalone JSON at end of content (common AI pattern)
  // Look for JSON object/array that appears after text
  const trailingJsonRegex = /\n\s*(\{[\s\S]*\}|\[[\s\S]*\])\s*$/;
  match = trailingJsonRegex.exec(content);
  if (match && !removedRanges.some(r => match.index >= r.start && match.index < r.end)) {
    try {
      const parsed = JSON.parse(match[1]);
      if (parsed && typeof parsed === 'object') {
        if (parsed.extracted_data) {
          extractions.push({
            data: injectFileName(parsed.extracted_data, parsed.file_name),
            title: parsed.file_name || 'Extraction Results'
          });
          removedRanges.push({ start: match.index, end: match.index + match[0].length });
        } else if (isExtractionData(parsed)) {
          extractions.push({ data: parsed, title: 'Extraction Results' });
          removedRanges.push({ start: match.index, end: match.index + match[0].length });
        }
      }
    } catch (e) {
      // Not valid JSON
    }
  }

  // Pattern 4: Content that is ONLY JSON (starts with { or [)
  const trimmedContent = content.trim();
  if (extractions.length === 0 && (trimmedContent.startsWith('{') || trimmedContent.startsWith('['))) {
    try {
      const parsed = JSON.parse(trimmedContent);
      if (parsed && typeof parsed === 'object') {
        if (parsed.extracted_data) {
          extractions.push({
            data: injectFileName(parsed.extracted_data, parsed.file_name),
            title: parsed.file_name || 'Extraction Results'
          });
          text = '';
        } else if (isExtractionData(parsed)) {
          extractions.push({ data: parsed, title: 'Extraction Results' });
          text = '';
        }
      }
    } catch (e) {
      // Not valid JSON
    }
  }

  // Remove extracted JSON from text
  if (removedRanges.length > 0 && text === content) {
    // Sort ranges in reverse order to remove from end first
    removedRanges.sort((a, b) => b.start - a.start);
    for (const range of removedRanges) {
      text = text.slice(0, range.start) + text.slice(range.end);
    }
    text = text.trim();
  }

  return { text, extractions };
};

export default ExtractionTable;
